/**
 * useAutoCreateWorkspaceFromOnboarding — Patch 19 stability fix.
 *
 * Bridges the demo/onboarding value-first flow into the authenticated
 * app. When a signed-in user has zero workspaces AND a saved onboarding
 * draft is present in localStorage, we transparently create a workspace
 * with that profile so they never see the "Not a member of this
 * workspace" state or a bare empty shell.
 *
 * Guarantees:
 *   • Runs at most once per browser session (idempotent flag).
 *   • Never fires while a guest session is active.
 *   • Never fires before the workspace list has actually been fetched
 *     (so we don't race the bootstrap query and create a duplicate).
 *   • Clears the onboarding draft after successful workspace creation so
 *     a subsequent login doesn't try to create another.
 *
 * If workspace creation fails, we swallow the error silently — the user
 * can still create a workspace manually from the topbar and nothing is
 * destroyed. This is a bridge, not a critical path.
 */
import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { useAuthStore } from "@/entities/auth/store";
import { useWorkspaceStore } from "@/app/store/workspace-store";
import { createWorkspace } from "@/entities/workspace/api";
import type { WorkspaceProfile } from "@/entities/workspace/model";

const ONBOARDING_STORAGE_KEY = "mos.onboarding.draft.v1";
const CONSUMED_FLAG_KEY = "mos.onboarding.draft.consumed_at";

interface OnboardingDraft {
  businessName?: string;
  industry?: string;
  website?: string;
  logoDataUrl?: string | null;
  goals?: string[];
  description?: string;
}

function slugify(input: string): string {
  const base = input.trim().toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
  if (base.length >= 3) return base;
  return `workspace-${Math.random().toString(36).slice(2, 8)}`;
}

function readDraft(): OnboardingDraft | null {
  try {
    const raw = window.localStorage.getItem(ONBOARDING_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as OnboardingDraft;
  } catch {
    return null;
  }
}

function markConsumed(): void {
  try {
    window.localStorage.setItem(CONSUMED_FLAG_KEY, new Date().toISOString());
    window.localStorage.removeItem(ONBOARDING_STORAGE_KEY);
  } catch { /* noop */ }
}

/** Best-effort profile hydration from the draft. Fields not provided
 *  fall back to sensible defaults so the backend never rejects the
 *  create call for missing required attributes. */
function toWorkspaceProfile(draft: OnboardingDraft): WorkspaceProfile {
  const name = (draft.businessName || "").trim() || "My Business";
  return {
    display_name: name,
    tagline: draft.description ? draft.description.slice(0, 140) : null,
    industry: draft.industry || null,
    logo_key: null, // Data URLs can't be uploaded here; onboarding photo will be attached later.
    primary_color: "#E5B454",
    accent_color: null,
    timezone: "UTC",
    locale: "en-US",
    currency: "USD",
  };
}

export function useAutoCreateWorkspaceFromOnboarding(): void {
  const token = useAuthStore((s) => s.accessToken);
  const guestActive = useAuthStore((s) => s.guest.active);
  const workspaces = useWorkspaceStore((s) => s.workspaces);
  const hydrated = useWorkspaceStore((s) => s.hydrated);
  const setWorkspaces = useWorkspaceStore((s) => s.setWorkspaces);
  const selectWorkspace = useWorkspaceStore((s) => s.selectWorkspace);
  const qc = useQueryClient();

  const inFlight = useRef(false);

  useEffect(() => {
    // Preconditions: signed in (not guest), bootstrap has finished, and
    // the user really has zero workspaces.
    if (!token) return;
    if (guestActive) return;
    if (!hydrated) return;
    if (workspaces.length !== 0) return;
    if (inFlight.current) return;

    const draft = readDraft();
    if (!draft) return;
    const businessName = (draft.businessName || "").trim();
    // Guard: if the draft is effectively empty, don't create a phantom
    // "Untitled" workspace — let the user do it explicitly.
    if (!businessName && !draft.description && (!draft.goals || draft.goals.length === 0)) return;

    inFlight.current = true;
    const slug = slugify(businessName || draft.description?.slice(0, 32) || "workspace");
    const profile = toWorkspaceProfile(draft);

    createWorkspace({ slug, profile })
      .then((ws) => {
        // Update the workspace store directly so any component reading
        // from Zustand sees the new workspace instantly. Then invalidate
        // the ["workspaces"] TanStack query so useBootstrapWorkspace
        // refetches the canonical list; because we set the currentId
        // *before* the refetch settles, its reconciliation (which
        // prefers the current id when found) will keep our selection.
        const list = useWorkspaceStore.getState().workspaces;
        setWorkspaces([...list, ws]);
        selectWorkspace(ws.id);
        // Also prime the query cache so subsequent readers see the row
        // without waiting for the next network round-trip.
        qc.setQueryData(["workspaces"], (prev: unknown) => {
          const arr = Array.isArray(prev) ? (prev as unknown[]) : [];
          return [...arr, ws];
        });
        qc.invalidateQueries({ queryKey: ["workspaces"] });
        markConsumed();
      })
      .catch((err) => {
        // Suppress. Slug collisions or transient failures shouldn't
        // block sign-in. The onboarding draft remains for a retry.
        // eslint-disable-next-line no-console
        console.debug("[onboarding-bridge] auto workspace creation skipped:", err?.message ?? err);
        inFlight.current = false;
      });
  }, [token, guestActive, hydrated, workspaces.length, setWorkspaces, selectWorkspace, qc]);
}
